import moment from 'moment';

// Reads the retry state that the reformatVideos and uploadYoutubeVideo Cloud
// Functions write on each car, so the Marketing tab and Feed Health page
// describe a stuck video the same way.
//   cars/{stock}.{clip}_status                 — set when a person must act
//   cars/{stock}.video_reformat_attempts.{clip} — per-clip conversion retries
//   cars/{stock}.video_upload                   — YouTube upload retries

export const VIDEO_KEYS = ['walkaround_video', 'engine_video', 'convertible_video', 'driving_video', 'undercarriage_video'];
export const REQUIRED_VIDEO_KEYS = ['walkaround_video', 'driving_video', 'undercarriage_video'];

export const CLIP_LABELS = {
  walkaround_video: 'Walk Around',
  engine_video: 'Engine Bay',
  convertible_video: 'Convertible Top',
  driving_video: 'Driving',
  undercarriage_video: 'Undercarriage',
};

export const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const retryWhen = (next) => (next && next > new Date() ? moment(next).fromNow() : 'on the next run');

// null when the clip is absent or already an mp4
export function clipState(car, key) {
  const path = car && car[key];
  if (!path || path.toLowerCase().endsWith('.mp4')) return null;
  const status = car[`${key}_status`];
  const attempts = (car.video_reformat_attempts || {})[key] || null;
  const next = attempts && toDate(attempts.next_attempt_at);
  const error = (attempts && attempts.last_error) || null;
  const since = attempts && toDate(attempts.first_failed_at);

  if (status === 'file missing - re-upload') {
    return { state: 'missing', text: 'File is missing from storage. Re-upload it.', color: 'error.main', error, since, retryable: false };
  }
  if (status === 'bad file - re-upload') {
    return { state: 'bad', text: 'File is unreadable. Re-upload it.', color: 'error.main', error, since, retryable: true };
  }
  if (status && status.startsWith('conversion failed')) {
    return { state: 'failing', text: `Conversion keeps failing. Retrying ${retryWhen(next)}.`, color: 'error.main', error, since, next, retryable: true };
  }
  if (error) {
    return { state: 'retrying', text: `Conversion failed. Retrying ${retryWhen(next)}.`, color: 'warning.dark', error, since, next, retryable: true };
  }
  return { state: 'converting', text: 'Converting to mp4…', color: 'warning.dark', retryable: false };
}

// null unless the car has an upload failure on record and no video yet
export function uploadState(car) {
  const upload = car && car.video_upload;
  if (!upload || !upload.last_error || car.youtube_link) return null;
  const next = toDate(upload.next_attempt_at);
  return {
    attempts: upload.attempts || 0,
    error: upload.last_error,
    since: toDate(upload.first_failed_at),
    next,
    text: `YouTube upload failed ${upload.attempts || 1}×. Retrying ${retryWhen(next)}.`,
  };
}

// Matches DAILY_UPLOAD_LIMIT in the uploadYoutubeVideo Cloud Function
export const DAILY_UPLOAD_LIMIT = 6;

const isMp4 = (path) => !!path && path.toLowerCase().includes('.mp4');

// Splits active cars with no YouTube video the way getActiveCarsWithoutVideo
// in the video Cloud Function does. `ready` is in upload order: cars that
// never failed first, then the failed car that waited longest.
export function youtubeQueue(cars) {
  const now = new Date();
  const ready = [];
  const retrying = [];
  const waiting = [];
  cars
    .filter(car => car.status === 'active' && !car.youtube_link)
    .sort((a, b) => a.id.localeCompare(b.id))
    .forEach(car => {
      const missing = REQUIRED_VIDEO_KEYS.filter(k => !isMp4(car[k]));
      const converting = ['engine_video', 'convertible_video']
        .filter(k => car[k] && !isMp4(car[k]) && !car[`${k}_status`]);
      const next = car.video_upload && toDate(car.video_upload.next_attempt_at);
      if (missing.length || converting.length) waiting.push({ car, missing, converting });
      else if (next && next > now) retrying.push({ car, next });
      else ready.push({ car });
    });
  const lastAttempt = (car) => (car.video_upload && toDate(car.video_upload.last_attempt_at)) || 0;
  ready.sort((a, b) => {
    if (!a.car.video_upload !== !b.car.video_upload) return a.car.video_upload ? 1 : -1;
    return lastAttempt(a.car) - lastAttempt(b.car);
  });
  return { ready, retrying, waiting };
}

// Cars the upload cron can take right now
export function countQueued(cars) {
  return youtubeQueue(cars).ready.length;
}
